import { useState, useEffect } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/utils/supabase';
import { LoanDocumentsPage } from './LoanDocumentsPage';
import type { ProspectLoan } from './types';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('LoanDocumentsWrapper', 'loan-documents');

/**
 * Wrapper component that handles URL-based routing for the loan documents page.
 * Fetches the loan by encompass_loan_id from URL params and renders LoanDocumentsPage.
 */
export function LoanDocumentsWrapper() {
  const { loanId } = useParams<{ loanId: string }>();
  const navigate = useNavigate();
  const [loan, setLoan] = useState<ProspectLoan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!loanId) {
      setError('No loan ID provided');
      setLoading(false);
      return;
    }

    const fetchLoan = async () => {
      log.info('Fetching loan by ID', { loanId });

      try {
        const { data, error: fetchError } = await supabase
          .from('encompass_loans')
          .select('*')
          .eq('encompass_loan_id', loanId)
          .maybeSingle();

        if (fetchError) {
          log.error('Failed to fetch loan', { loanId }, fetchError);
          setError('Failed to load loan details');
          return;
        }

        if (!data) {
          log.warn('Loan not found', { loanId });
          setError('Loan not found');
          return;
        }

        log.info('Loan fetched successfully', {
          loanId,
          loanNumber: data.trans_details_loan_number
        });
        setLoan(data as ProspectLoan);
      } catch (err) {
        log.error('Unexpected error fetching loan', { loanId }, err as Error);
        setError('An unexpected error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchLoan();
  }, [loanId]);

  // Loading state
  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        <span className="ml-2 text-gray-500">Loading loan documents...</span>
      </div>
    );
  }

  // Error state - redirect to internal dashboard
  if (error || !loan) {
    log.warn('Redirecting due to error or missing loan', { error, hasLoan: !!loan });
    return <Navigate to="/internal" replace />;
  }

  // Render the documents page with the fetched loan
  return (
    <LoanDocumentsPage
      loan={loan}
      onBack={() => navigate('/internal?section=live-loans')}
    />
  );
}

export default LoanDocumentsWrapper;
